/**
 * Upload a new file onto an existing governance document.
 *
 * The modal only picks the file. Once the user starts validation the run is
 * handed to `processingJobStore`, which owns the request and puts the
 * knowledge processing console up over the app - so the run can be minimised
 * to the floating status bubble and keeps going while the user works
 * elsewhere. The store reopens this picker (via `reopenPickerTick`) if the run
 * is cancelled or fails.
 */
import { useEffect, useState } from "react";
import { Upload, FileText, AlertTriangle, X } from "lucide-react";
import { Modal } from "../components/Modal";
import { useProcessingJobStore } from "../../stores/processingJobStore";
import { formatFileSize } from "./ContentRegistry/utils";

// ─── Constraints ─────────────────────────────────────────────────────────────

// Actual size limit is enforced server-side (currently 40MB per SYS-0005-E); this label is informational only.
const MAX_FILE_SIZE_LABEL = "40 MB";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentTitle: string;
  orgName?: string | null;
  brand?: string;
  docVersion?: string;
  docType?: string;
}

// ─── Main component ─────────────────────────────────────────────────────────

export function GovernanceUploadModal({
  open,
  onClose,
  documentId,
  documentTitle,
  orgName,
  docType,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const startJob = useProcessingJobStore((s) => s.start);
  /* Set when a previous run was cancelled or failed and the store sent the
     user back here; shown above the picker so the reason isn't lost. */
  const reopenTarget = useProcessingJobStore((s) => s.reopenTarget);
  const lastRunError =
    reopenTarget?.mode === "version" ? reopenTarget.error : null;

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPickError(null);
    setDragActive(false);
  }, [open]);

  // ── File picker handler ──────────────────────────────────────────────────
  const pickFile = (f: File | null) => {
    setPickError(null);
    if (!f) return;
    const name = f.name.toLowerCase();
    const isPdf = f.type === "application/pdf" || name.endsWith(".pdf");
    const isDoc =
      f.type === "application/msword" ||
      f.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      name.endsWith(".doc") ||
      name.endsWith(".docx");
    if (!isPdf && !isDoc) {
      setPickError("Only PDF and Word documents (.doc, .docx) are supported.");
      return;
    }
    setFile(f);
  };

  /**
   * Hand the run to the global job store and step out of the way - the
   * console takes over from `ProcessingJobHost`.
   */
  const startValidation = () => {
    if (!file) return;
    void startJob({
      mode: "version",
      docId: documentId,
      file,
      documentTitle,
      documentType: docType,
      fileName: file.name,
      fileSize: formatFileSize(file.size),
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Upload to: ${documentTitle}`}
      description={`Validation runs against ${orgName ?? "this org"}'s active rules before the file is saved.`}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={startValidation}
            disabled={!file}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 disabled:opacity-40"
          >
            Start Validation
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        {lastRunError && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm">
            <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-bold">{lastRunError.title}</p>
              <p className="mt-0.5 break-words">{lastRunError.message}</p>
            </div>
          </div>
        )}
        <label
          onDragEnter={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            pickFile(e.dataTransfer.files?.[0] ?? null);
          }}
          className={`flex flex-col items-center gap-3 ${file ? "p-4" : "p-8"} border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
            file
              ? "border-emerald-400 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/[0.04]"
              : dragActive
                ? "border-indigo-500 bg-indigo-50/40 dark:bg-indigo-500/5"
                : "border-border bg-[#f5f7fb] dark:bg-white/[0.02] hover:border-indigo-400 dark:hover:border-indigo-400"
          }`}
        >
          {file ? (
            <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-emerald-100/50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 text-[12px]">
              <div className="flex items-start gap-2 min-w-0">
                <FileText className="size-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col min-w-0">
                  <span className="text-emerald-800 dark:text-emerald-300 font-medium break-all">
                    {file.name}
                  </span>
                  <span className="text-[11px] text-emerald-600/40 dark:text-emerald-400/40">
                    {formatFileSize(file.size)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFile(null);
                }}
                className="p-1 rounded text-emerald-400 hover:text-red-500 transition-colors flex-shrink-0"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ) : (
            <>
              <Upload className="size-8 text-muted-foreground" />
              <div className="text-center space-y-0.5">
                <p className="text-sm font-medium text-foreground">
                  Drop a PDF or Word document here, or click to browse
                </p>
                <p className="text-xs text-muted-foreground">
                  We'll check it against{" "}
                  <span className="font-medium">
                    {orgName ? `${orgName}'s` : "your organization's"}
                  </span>{" "}
                  active rules before upload.
                </p>
                <p className="text-xs text-muted-foreground">
                  PDF · up to {MAX_FILE_SIZE_LABEL}
                </p>
              </div>
            </>
          )}
          <input
            type="file"
            accept="application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
        </label>
        {pickError && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm">
            <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
            <p>{pickError}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
